import {
	type CollectibleAuctionFront,
	type CollectibleAuctionListRefined,
	type CollectibleEventListRefined
} from '@capsicum/common';
import { useMainCurrenciesQuery } from '@capsicum/socios-core';
import { useMemo } from 'react';

//useCollectibleEventsQueries
import { useCollectibleAuctionsFrontQueries } from '../data-access/queries/useCollectibleEventsQueries';
import { auctionsUtils, parseAuctionFront } from '../util/auctionsUtils';

export const useAuctions = (options?: { auctionsPerEvent?: number; partnerIds?: Array<string> }) => {
	const { data: currencies } = useMainCurrenciesQuery();
	const { altCurrency, defaultCurrency } = currencies ?? {};
	const { getAwayTeam, getHomeTeam } = auctionsUtils();

	const params = {
		altCurrencyId: altCurrency?.id,
		auctionsPerEvent: options?.auctionsPerEvent || 5,
		mainCurrencyId: defaultCurrency?.id,
		// 'limit' parameter is set to 1000 on the BE
		...(options?.partnerIds?.length && { partnerIds: options.partnerIds })
	};

	const {
		data,
		isFetching,
		isFetchingNextPage,
		isLoading,
		refetch: refetchEvents
	} = useCollectibleAuctionsFrontQueries(params);

	const events: Array<CollectibleEventListRefined> = useMemo(() => {
		const eventsMap = new Map();

		if (data) {
			const allAuctions = data?.pages.flatMap(page => {
				return (page.data as { auctions?: Array<CollectibleAuctionFront> }) || [];
			}) as Array<CollectibleAuctionFront>;

			allAuctions.forEach(auction => {
				if (auction.event) {
					parseAuctionFront(auction);

					if (eventsMap.has(auction.event.id)) {
						eventsMap.get(auction.event.id).push(auction);
					} else {
						eventsMap.set(auction.event.id, [auction]);
					}
				}
			});

			return [...eventsMap].map(([eventId, auctions]) => {
				return {
					auctions,
					id: eventId,
					showSeeAllButton: auctions.length > 5,
					teamEvents: auctions[0]?.event?.teamsQualifier
				};
			});
		} else {
			return [];
		}
	}, [data]);

	const isEventClosed = (auctions: Array<CollectibleAuctionListRefined>) => {
		return !auctions.filter(auction => auction?.status !== 'finished').length;
	};

	return {
		events,
		getAwayTeam,
		getHomeTeam,
		isEventClosed,
		isFetching,
		isFetchingNextPage,
		isLoading,
		refetchEvents
	};
};
